/**
 * Staff idle sign-out, the screen's half (PM aa0f43a7).
 *
 * The club sets how many minutes a signed-in staff screen may sit untouched
 * (per person, or a club default). The backend says what this session's limit
 * is on every authenticated response (`X-Idle-Timeout-Minutes`, absent = no
 * limit) and refuses a request whose reported idle time is past it, with 401
 * STAFF_IDLE_TIMEOUT. See the backend's utils/staffIdleTimeout.js.
 *
 * Only the screen knows when a person last touched it, so this records the last
 * real interaction in localStorage, which every open tab shares: activity in
 * any tab keeps them all signed in. Every request then reports how long ago
 * that was (`X-Idle-Seconds`), in seconds rather than a timestamp so a device
 * whose clock is off is not signed out while in use.
 *
 * Both records are tied to the sign-in they belong to (the token's session
 * start), so the last person's idle time or limit on a shared computer never
 * applies to the next sign-in.
 *
 * The header is sent only after the backend has advertised a limit. That keeps
 * a session with no limit exactly as it is today, and proves the backend
 * allows the header, so this app can never deploy ahead of the backend's CORS.
 */

import { safeGetItem, safeRemoveItem, safeSetItem } from "@mediatechgroup/app-version";
import { getAuthToken } from "./apiHelpers";
import { tokenSessionStart } from "./authTokenStore";

export const IDLE_LIMIT_KEY = "staffIdleLimit";
export const LAST_ACTIVITY_KEY = "staffLastActivity";
export const IDLE_SECONDS_HEADER = "x-idle-seconds";
export const IDLE_LIMIT_RESPONSE_HEADER = "x-idle-timeout-minutes";
export const IDLE_MINUTES_MIN = 5;
export const IDLE_MINUTES_MAX = 1440;

/** Activity is written at most this often: mouse moves fire many times a second. */
const ACTIVITY_WRITE_EVERY_MS = 5 * 1000;

interface SessionRecord {
  /** The sign-in this belongs to (token session start, epoch seconds). */
  session: number;
}
interface LimitRecord extends SessionRecord {
  minutes: number;
}
interface ActivityRecord extends SessionRecord {
  /** Epoch ms of the last real interaction, on this device's clock. */
  at: number;
}

function currentSession(): number | null {
  try {
    return tokenSessionStart(getAuthToken());
  } catch {
    return null;
  }
}

function readRecord<T extends SessionRecord>(key: string): T | null {
  try {
    const raw = safeGetItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && typeof parsed.session === "number" ? (parsed as T) : null;
  } catch {
    return null;
  }
}

function writeRecord(key: string, value: SessionRecord): void {
  try {
    safeSetItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the server still enforces what it is told */
  }
}

/** A usable number of minutes, or null. */
export function validIdleMinutes(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < IDLE_MINUTES_MIN || n > IDLE_MINUTES_MAX) return null;
  return n;
}

/** This sign-in's idle limit in minutes, or null when it has none. */
export function getIdleLimitMinutes(): number | null {
  const session = currentSession();
  if (session === null) return null;
  const record = readRecord<LimitRecord>(IDLE_LIMIT_KEY);
  if (!record || record.session !== session) return null;
  return validIdleMinutes(record.minutes);
}

/**
 * Note what an authenticated response says about the limit. Only responses
 * that carry a refreshed token speak for the session; others are ignored.
 */
export function noteIdleLimitFromResponse(headers: Record<string, unknown> | undefined | null): void {
  if (!headers || !headers["x-refreshed-token"]) return;
  const session = currentSession();
  if (session === null) return;
  const minutes = validIdleMinutes(headers[IDLE_LIMIT_RESPONSE_HEADER]);
  if (minutes === null) {
    safeRemoveItem(IDLE_LIMIT_KEY);
    return;
  }
  const existing = readRecord<LimitRecord>(IDLE_LIMIT_KEY);
  if (existing?.session === session && existing.minutes === minutes) return;
  writeRecord(IDLE_LIMIT_KEY, { session, minutes } as LimitRecord);
}

/** The person touched the screen. `now` is injectable for tests. */
export function markActivity(now: number = Date.now()): void {
  const session = currentSession();
  if (session === null) return;
  const existing = readRecord<ActivityRecord>(LAST_ACTIVITY_KEY);
  if (existing?.session === session && now - existing.at < ACTIVITY_WRITE_EVERY_MS && now >= existing.at) return;
  writeRecord(LAST_ACTIVITY_KEY, { session, at: now } as ActivityRecord);
}

/**
 * Whole seconds since the person last touched any tab of this sign-in. A
 * sign-in with no record yet starts its clock now.
 */
export function idleSeconds(now: number = Date.now()): number | null {
  const session = currentSession();
  if (session === null) return null;
  const record = readRecord<ActivityRecord>(LAST_ACTIVITY_KEY);
  if (!record || record.session !== session || typeof record.at !== "number") {
    writeRecord(LAST_ACTIVITY_KEY, { session, at: now } as ActivityRecord);
    return 0;
  }
  return Math.max(0, Math.floor((now - record.at) / 1000));
}

/** The X-Idle-Seconds value to send, or null to send nothing (no limit). */
export function idleSecondsHeaderValue(now: number = Date.now()): string | null {
  if (getIdleLimitMinutes() === null) return null;
  const seconds = idleSeconds(now);
  return seconds === null ? null : String(seconds);
}

/** Forget both records (sign-out). */
export function clearStaffIdleSession(): void {
  safeRemoveItem(IDLE_LIMIT_KEY);
  safeRemoveItem(LAST_ACTIVITY_KEY);
}

/**
 * How a staff member's idle sign-out reads on their profile: "50 minutes",
 * "3 hours", "1 hour 30 minutes", or "Club default" when they have none.
 */
export function describeIdleSignOut(minutes: unknown): string {
  const n = validIdleMinutes(minutes);
  if (n === null) return "Club default";
  const hours = Math.floor(n / 60);
  const mins = n % 60;
  const h = hours ? `${hours} hour${hours === 1 ? "" : "s"}` : "";
  const m = mins ? `${mins} minute${mins === 1 ? "" : "s"}` : "";
  return [h, m].filter(Boolean).join(" ");
}
